import {
  Component,
  inject,
  input,
  output,
  DestroyRef,
  signal,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import {
  DialogService,
  MerchantService,
  VariableService,
} from '@services/index';
import { MerchantAutocompleteModel } from '../../merchant.models';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'merchant-card',
  imports: [NgTemplateOutlet],
  templateUrl: './merchant-card.component.html',
  styleUrl: './merchant-card.component.scss',
})
export class MerchantCardComponent {
  private merchantService = inject(MerchantService);
  private variableService = inject(VariableService);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  readonly merchant = input.required<MerchantAutocompleteModel>();
  readonly type = input<'column' | 'row'>('column');
  readonly action = input<'merchant-payment' | 'output'>('merchant-payment');
  readonly payWith = input<'account' | 'ticketing'>('account');
  readonly disabledFavoriteAction = input(false);
  readonly selectedMerchantEvent = output<MerchantAutocompleteModel>();

  isLoading = signal(false);
  baseHref = computed(() => this.variableService.BASE_HREF());

  makeFavoriteMerchants(favorite: MerchantAutocompleteModel, event: Event) {
    this.isLoading.set(true);
    event.stopPropagation();
    let body!: { merchant: string; merchant_action: string };
    if (!favorite.is_favorite_merchant) {
      body = {
        merchant: favorite.id.toString(),
        merchant_action: 'make_favorite',
      };
    } else if (favorite.is_favorite_merchant) {
      body = {
        merchant: favorite.id.toString(),
        merchant_action: 'revoke_favorite',
      };
    }
    this.merchantService
      .makeFavoriteMerchants(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          const data = result as {
            object: {
              response_data: MerchantAutocompleteModel;
              success: boolean;
              response_message: string;
              response_code: string;
            };
          };
          const response = data.object;
          if (response.success) {
            if (!favorite.is_favorite_merchant) {
              this.merchant().is_favorite_merchant = true;
              this.variableService.REFRESH_FAVORITE_MERCHANTS.set(true);
            } else {
              this.merchant().is_favorite_merchant = false;
              this.variableService.REFRESH_FAVORITE_MERCHANTS.set(true);
            }
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  selectMerchant() {
    const action = this.action();
    if (action === 'merchant-payment') {
      if (this.merchant().ticketing_enabled) {
        this.router.navigate([`${this.baseHref()}/events`, this.merchant().id]);
      } else if (this.merchant().gallery?.id) {
        this.router.navigate([`${this.baseHref()}/gallery/dashboard`]);
      } else {
        this.dialogService.openMerchantPaymentDialog({
          type: 'merchant',
          merchant: this.merchant(),
          payWith: this.payWith(),
        });
      }
    } else if (action === 'output') {
      this.selectedMerchantEvent.emit(this.merchant());
    }
  }
}
